import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, Typography, Chip, Stack, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { Tool } from '@procureai/agents';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolErrorIndicator } from '../ChatMessage/indicators/ChatToolErrorIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import { SvbrgSupplierText2CypherInput, SvbrgSupplierText2CypherOutput } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { useSupplierText2CypherResultDataGrid } from '~/routes/IntakeChat/components/SupplierText2CypherResult/SupplierText2CypherResult.dataGrid';

import { ToolResultComponent } from '.';

// Normalize results to always be an array
const normalizeResults = (tool: { state: string; result?: { results?: unknown } }): unknown[] => {
  if (tool.state !== 'result' || !tool.result?.results) {
    return [];
  }

  return Array.isArray(tool.result.results) ? tool.result.results : [tool.result.results];
};

const getQueryPreview = (query: string, maxLength = 100) => {
  const cleaned = query.replace(/\s+/g, ' ').trim();
  return cleaned.length > maxLength ? `${cleaned.substring(0, maxLength)}...` : cleaned;
};

export const SupplierText2CypherTool: ToolResultComponent<
  Tool.supplierText2Cypher,
  SvbrgSupplierText2CypherInput,
  SvbrgSupplierText2CypherOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });
  const apiRef = useGridApiRef();
  const [expanded, setExpanded] = useState(false);

  // Prepare results array for DataGrid configuration
  const resultsArray = normalizeResults(tool);

  const config = useSupplierText2CypherResultDataGrid(resultsArray as Record<string, unknown>[]);

  switch (tool.state) {
    case 'input-streaming':
    case 'input-available':
      return (
        <ChatLoadingIndicator
          message={t(`call`, {
            query: getQueryPreview(tool.input?.query ?? ''),
          })}
        />
      );

    case 'output-available': {
      if (tool.output.error) {
        return (
          <ChatToolErrorIndicator
            message={t(`error`, {
              error: tool.output.error,
            })}
          />
        );
      }

      const { metadata } = tool.output;
      const hasData = resultsArray.length > 0 && resultsArray[0] !== null && resultsArray[0] !== undefined;

      return (
        <ChatToolResultIndicator
          message={t(`result`, {
            count: metadata.rowCount,
            executionTime: metadata.executionTime,
          })}
          defaultOpen={true}
          disabled={false}
        >
          {/* Query metadata */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip label={t('metadata.rows', { count: metadata.rowCount })} size="small" variant="outlined" />
              <Chip
                label={t('metadata.executionTime', { time: metadata.executionTime })}
                size="small"
                variant="outlined"
              />
              {metadata.truncated && (
                <Chip label={t('metadata.truncated')} size="small" color="warning" variant="outlined" />
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {getQueryPreview(metadata.query, 200)}
            </Typography>
          </Box>
          {!hasData && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('noResults')}
            </Typography>
          )}
          {hasData && (
            <Box sx={{ mt: 1 }}>
              <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" color="text.secondary">
                    {expanded ? t('hideResults') : t('showResults', { count: metadata.rowCount })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DataGridPro
                    {...config}
                    rows={resultsArray.map((row, index) => ({ ...(row as Record<string, unknown>), _gridId: index }))}
                    getRowId={(row) => row._gridId as string | number}
                    sx={{
                      [`&.${gridClasses.root}`]: {
                        '--DataGrid-containerBackground': 'transparent',
                      },
                    }}
                    apiRef={apiRef}
                    hideFooter
                    disableColumnMenu
                    autoHeight
                    density="compact"
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 25 },
                      },
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </ChatToolResultIndicator>
      );
    }

    default:
      return null;
  }
};
